import { api } from './client';

export interface ControlledDocumentEmployee {
  id: string;
  name: string;
  email: string;
}

export interface ControlledDocumentListItem {
  id: string;
  typeKey: string;
  typeTitle: string;
  title: string;
  employeeId: string | null;
  employee: ControlledDocumentEmployee | null;
  currentBarcode: string;
  lifecycleStatus: string | null;
  dueAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ControlledDocumentFile {
  id: string;
  versionId: string;
  format: 'DOCX' | 'XLSX';
  storagePath: string;
  originalName: string | null;
  size: number;
  generatedAt: string;
}

export interface ControlledDocumentVersion {
  id: string;
  barcode: string;
  lifecycleStatus: string;
  dueAt: string | null;
  createdAt: string;
  isCurrent?: boolean;
  files?: ControlledDocumentFile[];
}

export interface ControlledDocumentDetail extends ControlledDocumentListItem {
  currentVersion: ControlledDocumentVersion | null;
  versions: ControlledDocumentVersion[];
  events: Array<{
    id: string;
    eventType: string;
    message: string | null;
    createdAt: string;
  }>;
}

export interface ControlledDocumentLookupResult {
  barcode: string;
  isCurrent: boolean;
  document: ControlledDocumentListItem;
  version: ControlledDocumentVersion;
  currentVersion: ControlledDocumentVersion | null;
}

export const controlledDocumentsApi = {
  list: async (params: {
    status?: string;
    scope?: string;
    search?: string;
    limit?: number;
  } = {}): Promise<{ items: ControlledDocumentListItem[]; nextCursor: string | null }> => {
    const { data } = await api.get('/controlled-documents', { params });
    return data;
  },

  detail: async (id: string): Promise<ControlledDocumentDetail> => {
    const { data } = await api.get(`/controlled-documents/${id}`);
    return data;
  },

  lookup: async (barcode: string): Promise<ControlledDocumentLookupResult> => {
    const { data } = await api.get(`/controlled-documents/lookup/${encodeURIComponent(barcode)}`);
    return data;
  },

  submit: async (barcode: string) => {
    const { data } = await api.post(`/controlled-documents/versions/${encodeURIComponent(barcode)}/submit`);
    return data;
  },

  downloadFile: async (id: string): Promise<Blob> => {
    const { data } = await api.get(`/controlled-documents/files/${id}/download`, { responseType: 'blob' });
    return data;
  },
};
